import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDraggable, useDroppable } from '@dnd-kit/core'

import { SECTIONS, id, sectionOf } from './fields'

// Список всех полей слева: поиск, разделы, перетаскивание в зоны отчёта.
// Двойной клик — быстрое добавление: разрез в строки, показатель в значения.
// Сам список — ещё и «корзина»: поле, перетащенное сюда из зоны, убирается из отчёта.

function FieldItem({ kind, name, label, section, used, onAdd }) {
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: id('field', kind, name) })

	return (
		<div ref={setNodeRef} {...listeners} {...attributes}
			className={`olap-field ${used ? 'olap-field--used' : ''} ${kind === 'measure' ? 'olap-field--measure' : ''}`}
			style={{ opacity: isDragging ? 0.4 : 1 }}
			onDoubleClick={() => onAdd(kind, name)}
			title={label}>
			<div className="olap-field__name">
				{kind === 'measure' ? <i className="uil uil-calculator-alt me-1"></i> : <i className="uil uil-list-ul me-1"></i>}
				{label}
				{used && <i className="uil uil-check ms-1 text-success"></i>}
			</div>
			<div className="olap-field__section">({section})</div>
		</div>
	)
}

// Разделы, в которых есть поля этого вида отчёта, — для кнопок над конструктором
export function fieldSections(dimensions) {
	return [
		'all',
		...SECTIONS.filter(item => item.fields.some(field => dimensions.includes(field))).map(item => item.key),
		...(dimensions.some(name => sectionOf(name) === 'other') ? ['other'] : []),
		'measures',
	]
}

function FieldList({ dimensions, measures, usedFields, dimensionLabel, measureLabel, onAdd, section }) {
	const { t } = useTranslation()
	const [search, setSearch] = useState('')
	const { setNodeRef, isOver } = useDroppable({ id: 'trash' })

	const items = useMemo(() => {
		const list = [
			...dimensions.map(name => ({ kind: 'dim', name, label: dimensionLabel(name), section: sectionOf(name) })),
			...measures.map(name => ({ kind: 'measure', name, label: measureLabel(name), section: 'measures' })),
		]
		const query = search.trim().toLowerCase()
		return list
			.filter(item => section === 'all' || item.section === section)
			.filter(item => !query || item.label.toLowerCase().includes(query))
			.sort((a, b) => a.label.localeCompare(b.label))
	}, [dimensions, measures, search, section]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div ref={setNodeRef} className={`olap-fields ${isOver ? 'olap-fields--trash' : ''}`}>
			<input type="text" className="olap-fields__search" value={search}
				placeholder={t('olap_fields_search')}
				onChange={(e) => setSearch(e.target.value)} />
			<div className="olap-fields__list">
				{items.map(item => (
					<FieldItem key={item.kind + item.name}
						kind={item.kind}
						name={item.name}
						label={item.label}
						section={t(`olap_section_${item.section}`)}
						used={usedFields.includes(item.kind + ':' + item.name)}
						onAdd={onAdd} />
				))}
				{items.length === 0 &&
					<div className="text-muted small p-2">{t('list_empty')}</div>
				}
			</div>
			{isOver && <div className="olap-fields__trash-hint">{t('olap_drop_to_remove')}</div>}
		</div>
	)
}

export default FieldList
